import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Save, LogOut, Inbox, X, Plus, Trash2 } from "lucide-react";
import { useContent } from "@/data/ContentContext";
import { SortableList } from "@/components/admin/SortableList";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useToken = () => {
  const [token, setToken] = useState(() =>
    typeof window !== "undefined" ? localStorage.getItem("admin_token") : null
  );
  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };
  return { token, logout };
};

const TABS = [
  { id: "hero", label: "Hero" },
  { id: "about", label: "About" },
  { id: "stats", label: "Stats" },
  { id: "skills", label: "Expertise" },
  { id: "career", label: "Career" },
  { id: "work", label: "Work" },
  { id: "testimonials", label: "Testimonials" },
  { id: "interviews", label: "Interviews" },
  { id: "inbox", label: "Inbox" },
];

const Field = ({ label, value, onChange, type = "input", rows = 3, "data-testid": testid }) => (
  <label className="block">
    <span className="overline block mb-1.5">{label}</span>
    {type === "textarea" ? (
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full bg-white border border-black/15 px-3 py-2 text-sm leading-relaxed focus:border-black outline-none"
        data-testid={testid}
      />
    ) : (
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-black/15 px-3 py-2 text-sm focus:border-black outline-none"
        data-testid={testid}
      />
    )}
  </label>
);

const SectionSave = ({ onSave, saving, status }) => (
  <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-[var(--bg)]/95 backdrop-blur border-t border-black/10 flex items-center gap-3">
    <button onClick={onSave} disabled={saving} className="cta-pill disabled:opacity-60" data-testid="admin-save-section">
      <Save size={14} />
      <span>{saving ? "Saving…" : "Save changes"}</span>
    </button>
    {status && <span className="text-sm text-black/60">{status}</span>}
  </div>
);

const Admin = () => {
  const { content, loading, updateSection } = useContent();
  const { token, logout } = useToken();
  const navigate = useNavigate();
  const [tab, setTab] = useState("hero");
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [inbox, setInbox] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  // Interviews template CMS — index of the currently-edited interview (null = list view)
  const [interviewIdx, setInterviewIdx] = useState(null);

  // initialise draft from loaded content
  useEffect(() => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  // Validate token AND role. Non-admin roles (e.g. lab guest) get bounced to /lab.
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (data?.role && data.role !== "admin") {
          navigate("/lab", { replace: true });
        }
      })
      .catch(() => {
        logout();
      });
  }, [token, logout, navigate]);

  const fetchInbox = async () => {
    setInboxLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInbox(data);
    } catch (e) {
      // ignore
    } finally {
      setInboxLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "inbox") fetchInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const save = async (section) => {
    setSaving(true);
    setStatus("");
    try {
      await updateSection(section, draft[section], token);
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setStatus("Save failed — try again.");
    } finally {
      setSaving(false);
    }
  };

  const set = (section, key, value) => {
    setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));
  };
  const setArrayItem = (section, idx, value) => {
    setDraft((d) => ({ ...d, [section]: d[section].map((v, i) => (i === idx ? value : v)) }));
  };
  const setListItem = (section, idx, key, value) => {
    setDraft((d) => ({
      ...d,
      [section]: d[section].map((v, i) => (i === idx ? { ...v, [key]: value } : v)),
    }));
  };
  const addItem = (section, blank) => {
    setDraft((d) => ({ ...d, [section]: [blank, ...(d[section] || [])] }));
  };
  const setList = (section, list) => setDraft((d) => ({ ...d, [section]: list }));
  const renumberWork = (list) => list.map((w, i) => ({ ...w, index: String(i + 1).padStart(2, "0") }));
  const removeItem = (section, idx) => {
    setDraft((d) => ({ ...d, [section]: d[section].filter((_, i) => i !== idx) }));
  };

  if (!token) return <Navigate to="/admin/login" replace />;
  if (loading || !draft.hero) {
    return (
      <div className="min-h-screen flex items-center justify-center text-black/60">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)] paper-grain">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[var(--bg)]/95 backdrop-blur border-b border-black/15">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <span className="font-display text-2xl">Admin</span>
            <span className="overline text-black/55">Mia O'Neill · Portfolio CMS</span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/lab"
              className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold bg-slate-900 text-white px-3 py-1.5 hover:bg-[#C2410C] transition-colors"
              title="Open AI Transformation Hub"
              data-testid="admin-open-lab"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              AI Hub
            </a>
            <button onClick={() => navigate("/")} className="overline link-underline">View site</button>
            <button onClick={logout} className="inline-flex items-center gap-2 overline link-underline">
              <LogOut size={14} /> Log out
            </button>
          </div>
        </div>
        {/* Tabs */}
        <nav className="max-w-[1200px] mx-auto px-6 flex flex-wrap gap-1 -mb-px">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold border-b-2 transition-colors ${
                tab === t.id ? "border-[var(--accent)] text-[var(--accent)]" : "border-transparent text-black/55 hover:text-black"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8">
        {/* HERO */}
        {tab === "hero" && (
          <section className="space-y-6 bg-white/40 border border-black/10 p-6">
            <h2 className="font-display text-3xl">Hero</h2>
            <div className="space-y-4">
              <div>
                <span className="overline block mb-2">Hero lines (one entry per line)</span>
                <textarea
                  value={(draft.hero?.lines || []).join("\n")}
                  onChange={(e) => set("hero", "lines", e.target.value.split("\n"))}
                  rows={3}
                  className="w-full bg-white border border-black/15 px-3 py-2 text-sm leading-relaxed focus:border-black outline-none"
                  data-testid="admin-hero-lines"
                />
              </div>
            </div>
            <SectionSave onSave={() => save("hero")} saving={saving} status={status} />
          </section>
        )}

        {/* ABOUT */}
        {tab === "about" && (
          <section className="space-y-6 bg-white/40 border border-black/10 p-6">
            <h2 className="font-display text-3xl">About</h2>
            <Field label="Overline" value={draft.about?.overline} onChange={(v) => set("about", "overline", v)} data-testid="admin-about-overline" />
            <Field label="Headline statement" value={draft.about?.statement} onChange={(v) => set("about", "statement", v)} type="textarea" rows={2} data-testid="admin-about-statement" />
            <div>
              <span className="overline block mb-2">Body paragraphs</span>
              <SortableList
                items={draft.about?.body || []}
                onChange={(list) => set("about", "body", list)}
                testid="admin-about-body"
                itemClassName="flex gap-2 mb-3 items-start"
                renderItem={(p, i, controls) => (
                  <>
                    <div className="pt-1">{controls}</div>
                    <textarea
                      value={p}
                      onChange={(e) => set("about", "body", draft.about.body.map((x, idx) => (idx === i ? e.target.value : x)))}
                      rows={3}
                      className="flex-1 bg-white border border-black/15 px-3 py-2 text-sm focus:border-black outline-none"
                    />
                    <button onClick={() => set("about", "body", draft.about.body.filter((_, idx) => idx !== i))}
                      className="p-2 text-black/40 hover:text-red-700"><Trash2 size={16} /></button>
                  </>
                )}
              />
              <button onClick={() => set("about", "body", ["", ...(draft.about?.body || [])])}
                className="overline link-underline inline-flex items-center gap-1"><Plus size={12} /> Add paragraph</button>
            </div>
            <div>
              <span className="overline block mb-2">Clients (one per line)</span>
              <textarea
                value={(draft.about?.clients || []).join("\n")}
                onChange={(e) => set("about", "clients", e.target.value.split("\n").filter(Boolean))}
                rows={6}
                className="w-full bg-white border border-black/15 px-3 py-2 text-sm focus:border-black outline-none"
              />
            </div>
            <SectionSave onSave={() => save("about")} saving={saving} status={status} />
          </section>
        )}

        {/* STATS */}
        {tab === "stats" && (
          <section className="space-y-6 bg-white/40 border border-black/10 p-6">
            <h2 className="font-display text-3xl">Stats</h2>
            <SortableList
              items={draft.stats || []}
              onChange={(list) => setList("stats", list)}
              testid="admin-stats"
              itemClassName="grid grid-cols-12 gap-3 items-end border-t border-black/10 pt-4"
              renderItem={(s, i, controls) => (
                <>
                  <div className="col-span-3"><Field label="Value" value={s.value} onChange={(v) => setListItem("stats", i, "value", Number(v) || 0)} /></div>
                  <div className="col-span-2"><Field label="Suffix" value={s.suffix} onChange={(v) => setListItem("stats", i, "suffix", v)} /></div>
                  <div className="col-span-5"><Field label="Label" value={s.label} onChange={(v) => setListItem("stats", i, "label", v)} /></div>
                  <div className="col-span-2 flex items-center justify-end gap-1">{controls}<button onClick={() => removeItem("stats", i)} className="p-2 text-black/40 hover:text-red-700"><Trash2 size={16} /></button></div>
                </>
              )}
            />
            <button onClick={() => addItem("stats", { value: 0, suffix: "", label: "" })} className="overline link-underline inline-flex items-center gap-1"><Plus size={12} /> Add stat</button>
            <SectionSave onSave={() => save("stats")} saving={saving} status={status} />
          </section>
        )}

        {/* SKILLS */}
        {tab === "skills" && (
          <section className="space-y-6 bg-white/40 border border-black/10 p-6">
            <h2 className="font-display text-3xl">Expertise</h2>
            <SortableList
              items={draft.skills || []}
              onChange={(list) => setList("skills", list)}
              testid="admin-skills"
              itemClassName="grid grid-cols-12 gap-3 items-end border-t border-black/10 pt-4"
              renderItem={(s, i, controls) => (
                <>
                  <div className="col-span-7"><Field label="Label" value={s.label} onChange={(v) => setListItem("skills", i, "label", v)} /></div>
                  <div className="col-span-3"><Field label="Color (hex)" value={s.color} onChange={(v) => setListItem("skills", i, "color", v)} /></div>
                  <div className="col-span-2 flex items-center justify-end gap-1">{controls}<button onClick={() => removeItem("skills", i)} className="p-2 text-black/40 hover:text-red-700"><Trash2 size={16} /></button></div>
                </>
              )}
            />
            <button onClick={() => addItem("skills", { label: "", color: "#C2410C" })} className="overline link-underline inline-flex items-center gap-1"><Plus size={12} /> Add expertise</button>
            <SectionSave onSave={() => save("skills")} saving={saving} status={status} />
          </section>
        )}

        {/* CAREER */}
        {tab === "career" && (
          <section className="space-y-6 bg-white/40 border border-black/10 p-6">
            <h2 className="font-display text-3xl">Career</h2>
            <p className="text-sm text-black/60">Drag the handle or use the arrows to reorder — the site shows roles in this order (newest first).</p>
            <SortableList
              items={draft.career || []}
              onChange={(list) => setList("career", list)}
              testid="admin-career"
              itemClassName="border-t border-black/10 pt-4 space-y-3"
              renderItem={(c, i, controls) => (
              <>
                <div className="flex justify-between items-center">
                  <span className="overline">Entry {i + 1}{c.company ? ` · ${c.company}` : ""}</span>
                  <div className="flex items-center gap-1">
                    {controls}
                    <button onClick={() => removeItem("career", i)} className="p-2 text-black/40 hover:text-red-700"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-6"><Field label="Company" value={c.company} onChange={(v) => setListItem("career", i, "company", v)} /></div>
                  <div className="col-span-6"><Field label="Role" value={c.role} onChange={(v) => setListItem("career", i, "role", v)} /></div>
                  <div className="col-span-4"><Field label="Period" value={c.period} onChange={(v) => setListItem("career", i, "period", v)} /></div>
                  <div className="col-span-4"><Field label="Duration" value={c.duration} onChange={(v) => setListItem("career", i, "duration", v)} /></div>
                  <div className="col-span-4"><Field label="Location" value={c.location} onChange={(v) => setListItem("career", i, "location", v)} /></div>
                  <div className="col-span-12"><Field label="Body" value={c.body} onChange={(v) => setListItem("career", i, "body", v)} type="textarea" rows={3} /></div>
                </div>
              </>
              )}
            />
            <button onClick={() => addItem("career", { company: "", role: "", period: "", duration: "", location: "", body: "" })}
              className="overline link-underline inline-flex items-center gap-1"><Plus size={12} /> Add role</button>
            <SectionSave onSave={() => save("career")} saving={saving} status={status} />
          </section>
        )}

        {/* WORK */}
        {tab === "work" && (
          <section className="space-y-6 bg-white/40 border border-black/10 p-6">
            <h2 className="font-display text-3xl">Project Highlights</h2>
            <p className="text-sm text-black/60">Edit copy fields below. Images / videos for each project are managed in code (ask me to swap any). The <code>id</code> must match an existing project to keep media wired up.</p>
            <p className="text-sm text-black/60">Reordering renumbers the index (01, 02…) automatically.</p>
            <SortableList
              items={draft.work || []}
              onChange={(list) => setList("work", renumberWork(list))}
              testid="admin-work"
              itemClassName="border-t border-black/10 pt-4 space-y-3"
              renderItem={(w, i, controls) => (
              <>
                <div className="flex justify-between items-center">
                  <span className="overline">{w.index} · {w.title}</span>
                  {controls}
                </div>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-2"><Field label="Index" value={w.index} onChange={(v) => setListItem("work", i, "index", v)} /></div>
                  <div className="col-span-3"><Field label="ID (don't change)" value={w.id} onChange={(v) => setListItem("work", i, "id", v)} /></div>
                  <div className="col-span-4"><Field label="Title" value={w.title} onChange={(v) => setListItem("work", i, "title", v)} /></div>
                  <div className="col-span-3"><Field label="Year" value={w.year} onChange={(v) => setListItem("work", i, "year", v)} /></div>
                  <div className="col-span-6"><Field label="Subtitle" value={w.subtitle} onChange={(v) => setListItem("work", i, "subtitle", v)} /></div>
                  <div className="col-span-6"><Field label="Role" value={w.role} onChange={(v) => setListItem("work", i, "role", v)} /></div>
                  <div className="col-span-12"><Field label="Tagline" value={w.tagline} onChange={(v) => setListItem("work", i, "tagline", v)} /></div>
                  <div className="col-span-12"><Field label="Preview (list copy)" value={w.preview} onChange={(v) => setListItem("work", i, "preview", v)} type="textarea" rows={2} /></div>
                  <div className="col-span-12"><Field label="Summary (modal overview)" value={w.summary} onChange={(v) => setListItem("work", i, "summary", v)} type="textarea" rows={4} /></div>
                  <div className="col-span-12">
                    <span className="overline block mb-2">Highlights (one per line)</span>
                    <textarea
                      value={(w.details || []).join("\n")}
                      onChange={(e) => setListItem("work", i, "details", e.target.value.split("\n").filter(Boolean))}
                      rows={5}
                      className="w-full bg-white border border-black/15 px-3 py-2 text-sm focus:border-black outline-none"
                    />
                  </div>
                  <div className="col-span-8"><Field label="Pull quote — text" value={w.pullQuote?.text} onChange={(v) => setListItem("work", i, "pullQuote", { ...(w.pullQuote || {}), text: v })} type="textarea" rows={2} /></div>
                  <div className="col-span-4"><Field label="Pull quote — author" value={w.pullQuote?.author} onChange={(v) => setListItem("work", i, "pullQuote", { ...(w.pullQuote || {}), author: v })} /></div>
                </div>
              </>
              )}
            />
            <SectionSave onSave={() => save("work")} saving={saving} status={status} />
          </section>
        )}

        {/* TESTIMONIALS */}
        {tab === "testimonials" && (
          <section className="space-y-6 bg-white/40 border border-black/10 p-6">
            <h2 className="font-display text-3xl">Testimonials</h2>
            <SortableList
              items={draft.testimonials || []}
              onChange={(list) => setList("testimonials", list)}
              testid="admin-testimonials"
              itemClassName="border-t border-black/10 pt-4 space-y-3"
              renderItem={(t, i, controls) => (
              <>
                <div className="flex justify-between items-center">
                  <span className="overline">{t.author || "(New testimonial)"}</span>
                  <div className="flex items-center gap-1">
                    {controls}
                    <button onClick={() => removeItem("testimonials", i)} className="p-2 text-black/40 hover:text-red-700"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-3">
                  <div className="col-span-5"><Field label="Author" value={t.author} onChange={(v) => setListItem("testimonials", i, "author", v)} /></div>
                  <div className="col-span-5"><Field label="Role" value={t.role} onChange={(v) => setListItem("testimonials", i, "role", v)} /></div>
                  <div className="col-span-2"><Field label="Initials" value={t.initials} onChange={(v) => setListItem("testimonials", i, "initials", v)} /></div>
                  <div className="col-span-6"><Field label="Relationship" value={t.relationship} onChange={(v) => setListItem("testimonials", i, "relationship", v)} /></div>
                  <div className="col-span-6"><Field label="Avatar colour (hex)" value={t.color} onChange={(v) => setListItem("testimonials", i, "color", v)} /></div>
                  <div className="col-span-12"><Field label="Quote" value={t.quote} onChange={(v) => setListItem("testimonials", i, "quote", v)} type="textarea" rows={5} /></div>
                </div>
              </>
              )}
            />
            <button
              onClick={() => addItem("testimonials", { author: "", role: "", quote: "", relationship: "", initials: "", color: "#C2410C" })}
              className="overline link-underline inline-flex items-center gap-1"><Plus size={12} /> Add testimonial</button>
            <SectionSave onSave={() => save("testimonials")} saving={saving} status={status} />
          </section>
        )}

        {/* INTERVIEWS TEMPLATE CMS */}
        {tab === "interviews" && (
          <section className="space-y-6 bg-white/40 border border-black/10 p-6" data-testid="admin-interviews-panel">
            {interviewIdx === null ? (
              // ---------- LIST VIEW ----------
              <>
                <div>
                  <h2 className="font-display text-3xl">Interview Pages</h2>
                  <p className="text-sm text-black/60 mt-2">
                    Bespoke presentation pages live at <code className="bg-black/5 px-1.5 py-0.5 text-xs">/interview/&lt;slug&gt;</code>.
                    Duplicate an existing one, then edit branding + copy for the new interview.
                  </p>
                </div>

                <div className="space-y-2" data-testid="admin-interviews-list">
                  <SortableList
                    items={draft.interviews || []}
                    onChange={(list) => setList("interviews", list)}
                    testid="admin-interviews"
                    itemClassName="grid grid-cols-12 gap-3 items-center border border-black/15 bg-white p-4"
                    renderItem={(it, i, controls) => (
                    <>
                      <div className="col-span-12 md:col-span-4 flex items-center gap-2">
                        {controls}
                        <div>
                        <div className="font-display text-xl">{it.company || "(Untitled)"}</div>
                        <div className="text-xs uppercase tracking-[0.18em] text-black/50 mt-0.5">{it.slug}</div>
                        </div>
                      </div>
                      <div className="col-span-12 md:col-span-4 text-sm text-black/60">
                        <a href={`/interview/${it.slug}`} target="_blank" rel="noopener noreferrer" className="link-underline break-all">
                          /interview/{it.slug}
                        </a>
                      </div>
                      <div className="col-span-12 md:col-span-4 flex flex-wrap gap-2 justify-end">
                        <button
                          onClick={() => setInterviewIdx(i)}
                          className="text-[11px] uppercase tracking-[0.22em] font-semibold border border-black px-3 py-1.5 hover:bg-black hover:text-[var(--bg)] transition-colors"
                          data-testid={`admin-interview-edit-${it.slug}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            const copy = JSON.parse(JSON.stringify(it));
                            copy.slug = `${it.slug}-copy`;
                            copy.company = `${it.company} (Copy)`;
                            setDraft((d) => ({ ...d, interviews: [copy, ...d.interviews] }));
                            setInterviewIdx(0);
                            setStatus("Duplicated — remember to save.");
                          }}
                          className="text-[11px] uppercase tracking-[0.22em] font-semibold border border-black/30 text-black/70 px-3 py-1.5 hover:border-black hover:text-black transition-colors"
                          data-testid={`admin-interview-duplicate-${it.slug}`}
                        >
                          Duplicate
                        </button>
                        {(draft.interviews || []).length > 1 && (
                          <button
                            onClick={() => {
                              if (!window.confirm(`Delete interview "${it.company}"? This cannot be undone.`)) return;
                              setDraft((d) => ({ ...d, interviews: d.interviews.filter((_, idx) => idx !== i) }));
                              setStatus("Removed from draft — remember to save.");
                            }}
                            className="text-[11px] uppercase tracking-[0.22em] font-semibold text-red-700/80 border border-red-700/20 px-3 py-1.5 hover:bg-red-700 hover:text-white hover:border-red-700 transition-colors"
                            data-testid={`admin-interview-delete-${it.slug}`}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </>
                    )}
                  />
                </div>

                <div className="pt-2 flex items-center gap-3">
                  <button
                    onClick={() => {
                      const base = draft.interviews?.[0] || {};
                      const blank = {
                        slug: `new-interview-${Date.now().toString().slice(-5)}`,
                        company: "New Company",
                        companyBadge: "COMPANY",
                        companyColor: "#0F0F0E",
                        heroHeadingLine1: "Role title",
                        heroHeadingLine2: "",
                        topBarLabel: "For new company",
                        showSection1: false,
                        showInterviewers: true,
                        interviewers: [{ name: "", role: "" }],
                        hero: { intro: "" },
                        questions: base.questions ? [...base.questions] : [],
                        strategyPriorities: [],
                        strengths: base.strengths ? [...base.strengths] : [],
                        cases: base.cases ? JSON.parse(JSON.stringify(base.cases)) : [],
                      };
                      setDraft((d) => ({ ...d, interviews: [blank, ...(d.interviews || [])] }));
                      setInterviewIdx(0);
                      setStatus("New draft interview — fill in the details and save.");
                    }}
                    className="cta-pill"
                    data-testid="admin-interview-new"
                  >
                    <Plus size={14} />
                    <span>New interview</span>
                  </button>
                  <SectionSave onSave={() => save("interviews")} saving={saving} status={status} />
                </div>
              </>
            ) : (
              // ---------- DETAIL EDITOR ----------
              (() => {
                const current = draft.interviews?.[interviewIdx];
                if (!current) return <div className="text-sm text-black/60">Interview not found. <button onClick={() => setInterviewIdx(null)} className="link-underline">Back to list</button></div>;
                const setF = (key, value) => setDraft((d) => ({
                  ...d,
                  interviews: d.interviews.map((x, i) => (i === interviewIdx ? { ...x, [key]: value } : x)),
                }));
                const setNested = (parent, key, value) => setDraft((d) => ({
                  ...d,
                  interviews: d.interviews.map((x, i) => (i === interviewIdx ? { ...x, [parent]: { ...(x[parent] || {}), [key]: value } } : x)),
                }));
                return (
                  <div className="space-y-8">
                    {/* Header row */}
                    <div className="flex items-baseline justify-between gap-4">
                      <div>
                        <button onClick={() => setInterviewIdx(null)} className="overline link-underline mb-1" data-testid="admin-interview-back">← All interviews</button>
                        <h2 className="font-display text-3xl">{current.company || "(Untitled)"}</h2>
                        <p className="text-sm text-black/60">
                          Live at <a href={`/interview/${current.slug}`} target="_blank" rel="noopener noreferrer" className="link-underline">/interview/{current.slug}</a>
                        </p>
                      </div>
                    </div>

                    {/* Branding */}
                    <div className="border-t border-black/10 pt-6">
                      <h3 className="font-display text-xl mb-4">Branding</h3>
                      <div className="grid grid-cols-12 gap-3">
                        <div className="col-span-6 md:col-span-3"><Field label="Slug (URL segment)" value={current.slug} onChange={(v) => setF("slug", v.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} data-testid="admin-int-slug" /></div>
                        <div className="col-span-6 md:col-span-3"><Field label="Company name" value={current.company} onChange={(v) => setF("company", v)} data-testid="admin-int-company" /></div>
                        <div className="col-span-6 md:col-span-3"><Field label="Company badge (short)" value={current.companyBadge} onChange={(v) => setF("companyBadge", v)} data-testid="admin-int-badge" /></div>
                        <div className="col-span-6 md:col-span-3"><Field label="Brand colour (hex)" value={current.companyColor} onChange={(v) => setF("companyColor", v)} data-testid="admin-int-color" /></div>
                        <div className="col-span-12 md:col-span-6"><Field label="Hero heading — line 1" value={current.heroHeadingLine1} onChange={(v) => setF("heroHeadingLine1", v)} data-testid="admin-int-hero1" /></div>
                        <div className="col-span-12 md:col-span-6"><Field label="Hero heading — line 2 (optional, orange)" value={current.heroHeadingLine2} onChange={(v) => setF("heroHeadingLine2", v)} data-testid="admin-int-hero2" /></div>
                        <div className="col-span-12 md:col-span-6"><Field label="Top-bar label" value={current.topBarLabel} onChange={(v) => setF("topBarLabel", v)} data-testid="admin-int-topbar" /></div>
                        <div className="col-span-12 md:col-span-6 flex items-end">
                          <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!current.showSection1}
                              onChange={(e) => setF("showSection1", e.target.checked)}
                              className="accent-black w-4 h-4"
                              data-testid="admin-int-section1"
                            />
                            Show "Understanding {current.company}" section (Section 01)
                          </label>
                        </div>
                      </div>
                      <p className="text-xs text-black/50 mt-2">
                        Section 01's stats, themes, industries, competitors and Strategy 2030 blocks are currently hard-coded for TÜV SÜD.
                        For other interviews, uncheck the section above until we make those blocks editable.
                      </p>
                    </div>

                    {/* Interviewer plate */}
                    <div className="border-t border-black/10 pt-6">
                      <div className="flex items-baseline justify-between mb-4">
                        <h3 className="font-display text-xl">Interviewer plate</h3>
                        <label className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!current.showInterviewers}
                            onChange={(e) => setF("showInterviewers", e.target.checked)}
                            className="accent-black w-4 h-4"
                            data-testid="admin-int-show-interviewers"
                          />
                          Show on page
                        </label>
                      </div>
                      <SortableList
                        items={current.interviewers || []}
                        onChange={(list) => setF("interviewers", list)}
                        testid="admin-int-interviewers"
                        itemClassName="grid grid-cols-12 gap-3 items-end mb-3"
                        renderItem={(p, i, controls) => (
                          <>
                            <div className="col-span-5"><Field label="Name" value={p.name} onChange={(v) => setF("interviewers", current.interviewers.map((x, idx) => idx === i ? { ...x, name: v } : x))} /></div>
                            <div className="col-span-5"><Field label="Role" value={p.role} onChange={(v) => setF("interviewers", current.interviewers.map((x, idx) => idx === i ? { ...x, role: v } : x))} /></div>
                            <div className="col-span-2 flex items-center justify-end gap-1">{controls}<button onClick={() => setF("interviewers", current.interviewers.filter((_, idx) => idx !== i))} className="p-2 text-black/40 hover:text-red-700"><Trash2 size={16} /></button></div>
                          </>
                        )}
                      />
                      <button onClick={() => setF("interviewers", [{ name: "", role: "" }, ...(current.interviewers || [])])} className="overline link-underline inline-flex items-center gap-1"><Plus size={12} /> Add interviewer</button>
                    </div>

                    {/* Hero intro */}
                    <div className="border-t border-black/10 pt-6">
                      <h3 className="font-display text-xl mb-4">Hero intro paragraph</h3>
                      <Field label="Intro" value={current.hero?.intro} onChange={(v) => setNested("hero", "intro", v)} type="textarea" rows={4} data-testid="admin-int-intro" />
                    </div>

                    {/* Questions */}
                    <div className="border-t border-black/10 pt-6">
                      <h3 className="font-display text-xl mb-2">Questions (Section 02)</h3>
                      <p className="text-xs text-black/55 mb-4">One question per line.</p>
                      <textarea
                        value={(current.questions || []).join("\n")}
                        onChange={(e) => setF("questions", e.target.value.split("\n").filter((l) => l.trim() !== ""))}
                        rows={12}
                        className="w-full bg-white border border-black/15 px-3 py-2 text-sm leading-relaxed focus:border-black outline-none font-mono"
                        data-testid="admin-int-questions"
                      />
                    </div>

                    {/* Strategy priorities */}
                    <div className="border-t border-black/10 pt-6">
                      <h3 className="font-display text-xl mb-2">Strategy priorities (Section 01)</h3>
                      <p className="text-xs text-black/55 mb-4">Only visible when Section 01 is enabled. One priority per line.</p>
                      <textarea
                        value={(current.strategyPriorities || []).join("\n")}
                        onChange={(e) => setF("strategyPriorities", e.target.value.split("\n").filter((l) => l.trim() !== ""))}
                        rows={7}
                        className="w-full bg-white border border-black/15 px-3 py-2 text-sm leading-relaxed focus:border-black outline-none font-mono"
                        data-testid="admin-int-priorities"
                      />
                    </div>

                    {/* Strengths */}
                    <div className="border-t border-black/10 pt-6">
                      <h3 className="font-display text-xl mb-2">Core strengths (Section 03)</h3>
                      <p className="text-xs text-black/55 mb-4">One per line.</p>
                      <textarea
                        value={(current.strengths || []).join("\n")}
                        onChange={(e) => setF("strengths", e.target.value.split("\n").filter((l) => l.trim() !== ""))}
                        rows={9}
                        className="w-full bg-white border border-black/15 px-3 py-2 text-sm leading-relaxed focus:border-black outline-none font-mono"
                        data-testid="admin-int-strengths"
                      />
                    </div>

                    {/* Cases */}
                    <div className="border-t border-black/10 pt-6">
                      <h3 className="font-display text-xl mb-2">Experience case studies (Section 05)</h3>
                      <p className="text-xs text-black/55 mb-4">
                        Case <code className="bg-black/5 px-1 text-[10px]">id</code> wires the case to its images and layout — don't change it.
                        Case <code className="bg-black/5 px-1 text-[10px]">rsg</code> uses Overview/Outcome/Challenges/Learnings; others use Summary + Highlights.
                      </p>
                      <SortableList
                        items={current.cases || []}
                        onChange={(list) => setF("cases", list)}
                        testid="admin-int-cases"
                        itemClassName="border border-black/15 bg-white p-4 mb-4"
                        renderItem={(c, i, controls) => {
                        const setCaseField = (key, value) => setF("cases", current.cases.map((x, idx) => (idx === i ? { ...x, [key]: value } : x)));
                        return (
                          <>
                            <div className="flex justify-between items-center mb-2">
                              <span className="overline">Case {i + 1}</span>
                              {controls}
                            </div>
                            <div className="grid grid-cols-12 gap-3 mb-4">
                              <div className="col-span-2"><Field label="ID" value={c.id} onChange={(v) => setCaseField("id", v)} /></div>
                              <div className="col-span-5"><Field label="Title" value={c.title} onChange={(v) => setCaseField("title", v)} /></div>
                              <div className="col-span-3"><Field label="Tag / subtitle" value={c.tag} onChange={(v) => setCaseField("tag", v)} /></div>
                              <div className="col-span-2"><Field label="Year" value={c.year} onChange={(v) => setCaseField("year", v)} /></div>
                            </div>
                            {c.id === "rsg" ? (
                              <div className="space-y-3">
                                <Field label="Overview" value={c.overview} onChange={(v) => setCaseField("overview", v)} type="textarea" rows={4} />
                                <Field label="Outcome" value={c.outcome} onChange={(v) => setCaseField("outcome", v)} type="textarea" rows={3} />
                                <div>
                                  <span className="overline block mb-2">Challenges (one per line)</span>
                                  <textarea value={(c.challenges || []).join("\n")} onChange={(e) => setCaseField("challenges", e.target.value.split("\n").filter((l) => l.trim() !== ""))} rows={6} className="w-full bg-white border border-black/15 px-3 py-2 text-sm focus:border-black outline-none" />
                                </div>
                                <div>
                                  <span className="overline block mb-2">Learnings (one per line)</span>
                                  <textarea value={(c.learnings || []).join("\n")} onChange={(e) => setCaseField("learnings", e.target.value.split("\n").filter((l) => l.trim() !== ""))} rows={6} className="w-full bg-white border border-black/15 px-3 py-2 text-sm focus:border-black outline-none" />
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <Field label="Summary" value={c.summary} onChange={(v) => setCaseField("summary", v)} type="textarea" rows={5} />
                                <div>
                                  <span className="overline block mb-2">Highlights (one per line)</span>
                                  <textarea value={(c.highlights || []).join("\n")} onChange={(e) => setCaseField("highlights", e.target.value.split("\n").filter((l) => l.trim() !== ""))} rows={5} className="w-full bg-white border border-black/15 px-3 py-2 text-sm focus:border-black outline-none" />
                                </div>
                              </div>
                            )}
                          </>
                        );
                        }}
                      />
                    </div>

                    <SectionSave onSave={() => save("interviews")} saving={saving} status={status} />
                  </div>
                );
              })()
            )}
          </section>
        )}

        {/* INBOX */}
        {tab === "inbox" && (
          <section className="space-y-6 bg-white/40 border border-black/10 p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-3xl">Inbox</h2>
              <button onClick={fetchInbox} className="overline link-underline">Refresh</button>
            </div>
            <p className="text-sm text-black/60">Briefs submitted via the contact form. A copy is also emailed to {process.env.REACT_APP_BACKEND_URL ? "your inbox" : ""}.</p>
            {inboxLoading ? (
              <div className="text-sm text-black/55">Loading…</div>
            ) : inbox.length === 0 ? (
              <div className="text-sm text-black/55">No submissions yet.</div>
            ) : (
              <ul className="space-y-3" data-testid="admin-inbox-list">
                {inbox.map((m) => (
                  <li key={m.id} className="bg-white border border-black/10 p-4">
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <div className="font-display text-lg">{m.name}</div>
                      <div className="text-xs text-black/55">{new Date(m.timestamp).toLocaleString()}</div>
                    </div>
                    <div className="text-sm text-black/70 mb-2">
                      <a href={`mailto:${m.email}`} className="link-underline">{m.email}</a>
                      {m.company ? ` · ${m.company}` : ""}
                    </div>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default Admin;
